import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';

import BrandedButton from '@/components/buttons/BrandedButton';
import TextInputField from '@/components/inputs/TextInputField';
import { BillingAddressData, billingAddressResolver } from '@/lib/validations/PaymentMethodsSchema';
import { parseAddressComponents } from '@/utils/addressFromAddressComponents';
import { i18n } from '@/utils/i18n';

import GoogleAutoComplete from '../inputs/GoogleAutocomplete';
import Text from '../Text';
import FormWrapper from './FormWrapper';

interface BillingAddressFormProps {
  initialData?: BillingAddressData;
  onDone?: (data: BillingAddressData) => void;
  doneLabel?: string;
  isLoading?: boolean;
}

const BillingAddressForm = ({
  initialData,
  onDone,
  doneLabel = i18n.t('mix.done'),
  isLoading = false
}: BillingAddressFormProps) => {
  const {
    control,
    handleSubmit,
    setFocus,
    reset,
    setValue,
    setError,
    trigger,
    formState: { isValid, errors }
  } = useForm<BillingAddressData>({
    defaultValues: initialData,
    mode: 'onChange',
    resolver: billingAddressResolver
  });

  const onSubmit: SubmitHandler<BillingAddressData> = async (data) => {
    if (onDone) {
      onDone(data);
    }
  };

  useEffect(() => {
    if (initialData) {
      reset(initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  return (
    <FormWrapper horizontalPadding={0}>
      <View style={styles.formContainer}>
        <View style={styles.inputsContainer}>
          <TextInputField
            name="firstName"
            label={i18n.t('billing_address.name_label')}
            placeholder={i18n.t('billing_address.name_placeholder')}
            control={control}
            returnKeyLabel="next"
            returnKeyType="next"
            onSubmitEditing={() => setFocus('lastName')}
          />
          <TextInputField
            name="lastName"
            label={i18n.t('billing_address.surname_label')}
            placeholder={i18n.t('billing_address.surname_placeholder')}
            control={control}
            returnKeyLabel="next"
            returnKeyType="next"
          />

          <View style={styles.autoComplete}>
            <GoogleAutoComplete
              initialValue={initialData?.address1.replace(/,\s*\d{5}(?:-\d{4})?,/, ',')} //Remove zip code from initial value (only visual)
              label={i18n.t('billing_address.address_label')}
              placeholder={i18n.t('billing_address.address_placeholder')}
              predictionsScrollEnabled={false}
              type={'address'}
              error={errors.address1}
              showManualEntry
              onChange={(result) => {
                if (!result?.postalCode || result.postalCode === '' || !result.city || result.city === '') {
                  setError('address1', {
                    message: i18n.t('mix.enter_valid_address')
                  });
                } else if (result) {
                  const components = parseAddressComponents(result.addressComponents);
                  setValue(
                    'address1',
                    `${components.street}, ${components.city}, ${components.state} ${components.postalCode}, ${components.country}`
                  );
                  trigger('address1');
                }
              }}
            />
          </View>

          <TextInputField
            name="email"
            label={i18n.t('billing_address.email_label')}
            placeholder={i18n.t('billing_address.email_placeholder')}
            control={control}
            type="email-address"
            returnKeyLabel="next"
            returnKeyType="next"
            onSubmitEditing={() => setFocus('emailConfirmation')}
          />

          <TextInputField
            name="emailConfirmation"
            label={i18n.t('billing_address.email_confirmation_label')}
            placeholder={i18n.t('billing_address.email_confirmation_placeholder')}
            control={control}
            type="email-address"
            returnKeyLabel="done"
            returnKeyType="done"
          />
        </View>
        {errors.root !== null && <Text style={styles.errorText}>{errors?.root?.message}</Text>}

        <BrandedButton loading={isLoading} onPress={handleSubmit(onSubmit)} disabled={!isValid}>
          {doneLabel}
        </BrandedButton>
      </View>
    </FormWrapper>
  );
};

const styles = StyleSheet.create({
  formContainer: {
    flex: 1,
    justifyContent: 'space-between'
  },

  inputsContainer: {
    gap: 15
  },

  title: {
    fontSize: 22,
    color: 'black',
    alignSelf: 'center'
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  },

  autoComplete: {
    zIndex: 1000
  }
});

export default BillingAddressForm;
